import React from 'react';

/**
 * Shimmer skeletons.
 *
 * A sweeping highlight rather than `animate-pulse` (which only fades opacity):
 * the sweep reads as "content is arriving" instead of "something is blinking".
 *
 * Implementation notes:
 *  - The sweep is a translated child, not an animated background-position.
 *    Transform stays on the compositor, so a page full of skeletons doesn't
 *    thrash the main thread while data is still loading.
 *  - `motion-reduce:animate-none` honours prefers-reduced-motion; the muted
 *    block remains, so the layout still communicates loading without motion.
 *  - The whole tree is aria-hidden and the container carries the live region,
 *    so screen readers hear "Loading…" once instead of narrating placeholders.
 */
export const Skeleton = ({ className = '', rounded = 'rounded-md' }) => (
  <span
    aria-hidden="true"
    className={`relative block overflow-hidden bg-gray-100 ${rounded} ${className}`}
  >
    <span className="absolute inset-0 -translate-x-full animate-shimmer motion-reduce:animate-none bg-gradient-to-r from-transparent via-white/70 to-transparent" />
  </span>
);

/** Multi-line text block. The last line is short, the way real text wraps. */
export const SkeletonText = ({ lines = 3, className = '' }) => (
  <span className={`block space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
    ))}
  </span>
);

export const SkeletonCircle = ({ size = 40, className = '' }) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={`relative block overflow-hidden bg-gray-100 rounded-full shrink-0 ${className}`}
  >
    <span className="absolute inset-0 -translate-x-full animate-shimmer motion-reduce:animate-none bg-gradient-to-r from-transparent via-white/70 to-transparent" />
  </span>
);

/**
 * Wraps a group of skeletons so assistive tech announces the load once.
 * Use around any skeleton cluster that replaces real content.
 */
export const SkeletonGroup = ({ label = 'Loading…', className = '', children }) => (
  <div role="status" aria-live="polite" aria-busy="true" className={className}>
    <span className="sr-only">{label}</span>
    {children}
  </div>
);

/** Mirrors the search-result card: image, price row, spec, location, chips, buttons. */
export const PropertyCardSkeleton = () => (
  <div className="bg-card border border-border rounded-lg overflow-hidden">
    <Skeleton rounded="rounded-none" className="h-48 w-full" />
    <div className="p-4">
      <div className="flex items-start justify-between mb-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-14" />
      </div>
      <Skeleton className="h-4 w-3/4 mb-2" />
      <Skeleton className="h-3 w-1/2 mb-4" />
      <div className="flex gap-2 mb-4">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-14" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 flex-1" rounded="rounded-md" />
        <Skeleton className="h-9 flex-1" rounded="rounded-md" />
      </div>
    </div>
  </div>
);

/** Horizontal variant used by the list (non-grid) view. */
export const PropertyRowSkeleton = () => (
  <div className="bg-card border border-border rounded-lg p-4">
    <div className="flex gap-4">
      <Skeleton className="w-48 h-32 shrink-0" rounded="rounded-lg" />
      <div className="flex-1 space-y-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-24" />
        </div>
      </div>
    </div>
  </div>
);

export default Skeleton;
